import { Trans, useLingui } from "@lingui/react/macro";
import type { VariantProps } from "class-variance-authority";

import { Badge, type badgeVariants } from "@/components/ui/badge";

import { PLATFORM_LABEL, type AdPlatform } from "./types";

/**
 * Status + platform badges for the Ads console. Colour comes only from the
 * Badge variants (brand tokens) — never a raw Tailwind colour map.
 */

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

export function PlatformBadge({ platform }: { platform: AdPlatform | null }) {
  if (!platform) return null;
  return (
    <Badge variant={platform === "meta" ? "info" : "accent"} className="text-[0.625rem]">
      {PLATFORM_LABEL[platform]}
    </Badge>
  );
}

const CONNECTION_VARIANT: Record<string, BadgeVariant> = {
  active: "success",
  pending: "neutral",
  error: "destructive",
  expired: "warning",
  revoked: "warning",
  disconnected: "warning",
  paused: "warning",
  disabled: "neutral",
};

export function ConnectionStatusBadge({ status }: { status: string }) {
  const { t } = useLingui();
  const label = (() => {
    switch (status) {
      case "active":
        return t`Active`;
      case "pending":
        return t`Pending`;
      case "error":
        return t`Error`;
      case "expired":
        return t`Token expired`;
      case "revoked":
        return t`Access revoked`;
      case "disconnected":
        return t`Disconnected`;
      case "paused":
        return t`Paused`;
      case "disabled":
        return t`Disabled`;
      default:
        return status;
    }
  })();
  return (
    <Badge variant={CONNECTION_VARIANT[status] ?? "outline"} className="text-[0.625rem]">
      {label}
    </Badge>
  );
}

const CAMPAIGN_VARIANT: Record<string, BadgeVariant> = {
  active: "success",
  enabled: "success",
  paused: "warning",
  removed: "neutral",
  deleted: "neutral",
  archived: "neutral",
};

export function AdCampaignStatusBadge({ status }: { status: string | null }) {
  const { t } = useLingui();
  const value = (status ?? "").toLowerCase();
  const label = (() => {
    switch (value) {
      case "active":
      case "enabled":
        return t`Active`;
      case "paused":
        return t`Paused`;
      case "removed":
      case "deleted":
        return t`Removed`;
      case "archived":
        return t`Archived`;
      case "":
        return t`Unknown`;
      default:
        return status ?? "";
    }
  })();
  return (
    <Badge variant={CAMPAIGN_VARIANT[value] ?? "outline"} className="text-[0.625rem]">
      {label}
    </Badge>
  );
}

const CONVERSION_VARIANT: Record<string, BadgeVariant> = {
  pending: "neutral",
  sent: "success",
  failed: "destructive",
  skipped: "warning",
};

export function ConversionStatusBadge({ status }: { status: string }) {
  const label = useConversionStatusLabel()(status);
  return (
    <Badge variant={CONVERSION_VARIANT[status] ?? "outline"} className="text-[0.625rem]">
      {label}
    </Badge>
  );
}

/** Translated label for an AdConversionEvent.status (also used by the filter chips). */
export function useConversionStatusLabel() {
  const { t } = useLingui();
  return (status: string): string => {
    switch (status) {
      case "pending":
        return t`Pending`;
      case "sent":
        return t`Sent`;
      case "failed":
        return t`Failed`;
      case "skipped":
        return t`Skipped`;
      default:
        return status;
    }
  };
}

/**
 * Translated reason for a conversion the outbox closed as `skipped` — the code
 * sits in `lastError` (packages/marketing ads/conversions.ts,
 * ads/consent-withdrawal.ts). Null for a code this build does not know; the
 * table then shows it verbatim, so a new reason is visible, never blank.
 */
export function useConversionSkipReasonLabel() {
  const { t } = useLingui();
  return (code: string): string | null => {
    switch (code) {
      case "consent_withdrawn":
      case "ads_consent_withdrawn":
        return t`Ads consent withdrawn — never sent`;
      case "lead_deleted":
        return t`Lead erased — never sent`;
      case "no_gclid":
      case "no_fbclid":
      case "no_click_id":
        return t`No click id from this platform — nothing to match it on, not sent`;
      case "not_b2b_lead":
        return t`Not a B2B lead — never sent`;
      case "unsupported_event":
        return t`Event type not supported`;
      case "pixel_not_configured":
        return t`Pixel / dataset id not set on the account`;
      case "conversion_action_not_configured":
        return t`Conversion action not set on the account`;
      case "gclid_older_than_90_days":
        return t`Google click older than 90 days`;
      default:
        return null;
    }
  };
}

/** Translated name of a B2B conversion event. */
export function ConversionEventLabel({ eventName }: { eventName: string }) {
  switch (eventName) {
    case "profile_claimed":
      return <Trans>Profile claimed</Trans>;
    case "paid_subscription":
      return <Trans>First paid subscription</Trans>;
    default:
      return <>{eventName}</>;
  }
}

/** "Auto-paused" marker for a campaign the circuit breaker stopped. */
export function AutoPausedBadge({ reason }: { reason: string | null }) {
  return (
    <Badge variant="warning" className="text-[0.625rem]" title={reason ?? undefined}>
      <Trans>Auto-paused</Trans>
    </Badge>
  );
}
